// Thin DOM-facing wrapper around logic.ts's pure hash parse/serialise: owns the URL hash as the
// single source of truth for app state (docs/DESIGN.md §6.1). Updates use history.replaceState
// (no history spam); hashchange re-renders.
import { defaultState, parseHash, serializeHash, type AppState } from "./logic";
import type { PlzIndex, Registry } from "./registry";

export type StateListener = (state: AppState) => void;

export class AppStateStore {
  private state: AppState;
  private readonly listeners = new Set<StateListener>();

  constructor(
    private readonly registry: Registry,
    private readonly plzIndex: PlzIndex,
  ) {
    this.state = parseHash(location.hash, registry, plzIndex);
    // Make sure the address bar reflects a fully-resolved state even on first load.
    history.replaceState(null, "", serializeHash(this.state));
    window.addEventListener("hashchange", () => {
      this.state = parseHash(location.hash, this.registry, this.plzIndex);
      this.notify();
    });
  }

  get(): AppState {
    return this.state;
  }

  /** Merge a partial update into state and push it to the URL hash. */
  set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    history.replaceState(null, "", serializeHash(this.state));
    this.notify();
  }

  reset(): void {
    this.set(defaultState(this.registry, this.plzIndex));
  }

  subscribe(fn: StateListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    for (const fn of this.listeners) fn(this.state);
  }
}
